import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SlidersHorizontal, X, ChevronDown } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { getProducts, getBrands, getCategories } from '../api';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filters from URL
  const category = searchParams.get('category') || '';
  const brand = searchParams.get('brand') || '';
  const search = searchParams.get('search') || '';
  const isHot = searchParams.get('is_hot') === 'true';
  const isLimited = searchParams.get('is_limited') === 'true';
  const sortBy = searchParams.get('sort') || 'newest';

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [brandsRes, categoriesRes] = await Promise.all([
          getBrands(),
          getCategories()
        ]);
        setBrands(brandsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Failed to fetch filters:', error);
      }
    };
    fetchFilters();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = {};
        if (category) params.category = category;
        if (brand) params.brand = brand;
        if (search) params.search = search;
        if (isHot) params.is_hot = true;
        if (isLimited) params.is_limited = true;

        const response = await getProducts(params);
        let data = response.data;

        // Sort client-side
        if (sortBy === 'price-low') {
          data.sort((a, b) => a.price - b.price);
        } else if (sortBy === 'price-high') {
          data.sort((a, b) => b.price - a.price);
        } else if (sortBy === 'rating') {
          data.sort((a, b) => b.rating - a.rating);
        }

        setProducts(data);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category, brand, search, isHot, isLimited, sortBy]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const activeFiltersCount = [category, brand, search, isHot, isLimited].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-6 lg:px-12" data-testid="shop-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-display text-5xl sm:text-6xl uppercase mb-4">
              {category ? category.toUpperCase() : search ? `"${search}"` : 'ALL PRODUCTS'}
            </h1>
            <p className="text-white/60">
              {products.length} products found
            </p>
          </motion.div>
        </div>

        {/* Filters Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-8 border-b border-white/10">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              className="btn-secondary py-2 px-4 flex items-center gap-2"
              onClick={() => setFiltersOpen(!filtersOpen)}
              data-testid="filters-btn"
            >
              <SlidersHorizontal size={18} />
              FILTERS
              {activeFiltersCount > 0 && (
                <span className="bg-[#D2FF00] text-black text-xs px-2 py-0.5 rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </Button>

            {activeFiltersCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-white/60 text-sm hover:text-[#D2FF00] flex items-center gap-1"
              >
                <X size={14} />
                Clear all
              </button>
            )}
          </div>

          <Select value={sortBy} onValueChange={(value) => updateFilter('sort', value)}>
            <SelectTrigger className="w-[180px] bg-transparent border-white/20 text-white" data-testid="sort-select">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price-low">Price: Low to High</SelectItem>
              <SelectItem value="price-high">Price: High to Low</SelectItem>
              <SelectItem value="rating">Top Rated</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-12">
          {/* Filters Sidebar */}
          {filtersOpen && (
            <motion.aside
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="w-64 flex-shrink-0"
            >
              {/* Categories */}
              <div className="filter-section">
                <h3 className="font-display text-lg uppercase mb-4">CATEGORY</h3>
                <div className="space-y-3">
                  {categories.map((cat) => (
                    <label key={cat} className="flex items-center gap-3 cursor-pointer group">
                      <Checkbox
                        checked={category === cat}
                        onCheckedChange={(checked) => updateFilter('category', checked ? cat : '')}
                        className="border-white/20 data-[state=checked]:bg-[#D2FF00] data-[state=checked]:border-[#D2FF00]"
                      />
                      <span className="text-white/60 text-sm capitalize group-hover:text-white transition-colors">
                        {cat}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Brands */}
              <div className="filter-section">
                <h3 className="font-display text-lg uppercase mb-4">BRAND</h3>
                <div className="space-y-3">
                  {brands.map((b) => (
                    <label key={b} className="flex items-center gap-3 cursor-pointer group">
                      <Checkbox
                        checked={brand === b}
                        onCheckedChange={(checked) => updateFilter('brand', checked ? b : '')}
                        className="border-white/20 data-[state=checked]:bg-[#D2FF00] data-[state=checked]:border-[#D2FF00]"
                      />
                      <span className="text-white/60 text-sm group-hover:text-white transition-colors">
                        {b}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div className="filter-section">
                <h3 className="font-display text-lg uppercase mb-4">TAGS</h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <Checkbox
                      checked={isHot}
                      onCheckedChange={(checked) => updateFilter('is_hot', checked ? 'true' : '')}
                      className="border-white/20 data-[state=checked]:bg-[#FF3B30] data-[state=checked]:border-[#FF3B30]"
                    />
                    <span className="text-white/60 text-sm group-hover:text-white transition-colors">
                      Hot Drops
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <Checkbox
                      checked={isLimited}
                      onCheckedChange={(checked) => updateFilter('is_limited', checked ? 'true' : '')}
                      className="border-white/20 data-[state=checked]:bg-[#D2FF00] data-[state=checked]:border-[#D2FF00]"
                    />
                    <span className="text-white/60 text-sm group-hover:text-white transition-colors">
                      Limited Edition
                    </span>
                  </label>
                </div>
              </div>
            </motion.aside>
          )}

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(9)].map((_, i) => (
                  <div key={i} className="aspect-square skeleton" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-24">
                <p className="text-white/60 text-lg mb-4">No products found</p>
                <Button onClick={clearFilters} className="btn-primary">
                  Clear Filters
                </Button>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
